import { useCallback, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import {
  GAMES,
  GAME_KEYS,
  drawPortfolio,
  largestOverlap,
  maxOverlapFor,
  nextDraw,
} from '../engine';
import type { GameKey, Grid } from '../engine';
import { GridCard, gridText } from '../components/GridCard';
import { formatDateLong, formatDecimal, formatEuros, formatInt } from '../lib/format';
import { newId, saveGrids } from '../lib/storage';
import type { SavedGrid } from '../lib/storage';
import { comboPopularity } from '../engine';

const MIN_GRIDS = 1;
const MAX_GRIDS = 8;
/** Above this, grids become so systematically high they look like each other again. */
const LAMBDA_CEILING = 2.5;

export function Generator(): ReactElement {
  const [gameKey, setGameKey] = useState<GameKey>('loto');
  const [count, setCount] = useState(2);
  const [advanced, setAdvanced] = useState(false);
  const [lambdaOverride, setLambdaOverride] = useState<number | null>(null);
  const [grids, setGrids] = useState<Grid[] | null>(null);
  const [elapsed, setElapsed] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const game = GAMES[gameKey];
  const lambda = lambdaOverride ?? game.lambda;
  const next = useMemo(() => nextDraw(game), [game]);

  const generate = useCallback((): void => {
    setError(null);
    setSaved(false);
    const t0 = performance.now();
    try {
      const options = lambdaOverride === null ? {} : { lambda: lambdaOverride };
      setGrids(drawPortfolio(game, count, options));
      setElapsed(performance.now() - t0);
    } catch (e) {
      setGrids(null);
      setElapsed(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [game, count, lambdaOverride]);

  const saveAll = useCallback((): void => {
    if (grids === null) return;
    const now = new Date().toISOString();
    const records: SavedGrid[] = grids.map((g) => ({
      id: newId(),
      game: gameKey,
      main: [...g.main],
      bonus: [...g.bonus],
      drawDate: next.date,
      savedAt: now,
      lambda,
      popularity: comboPopularity(g.main, game.n),
    }));
    void saveGrids(records)
      .then(() => setSaved(true))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [grids, gameKey, next.date, lambda, game.n]);

  const overlap = grids !== null && grids.length > 1 ? largestOverlap(grids) : null;

  return (
    <div className="screen">
      <h1>Générateur</h1>
      <p className="lede">
        Tirage pondéré pour éviter les combinaisons que la foule joue. La probabilité de
        gagner reste strictement inchangée.
      </p>

      <div className="segmented" role="group" aria-label="Jeu">
        {GAME_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={gameKey === key}
            onClick={() => {
              setGameKey(key);
              setGrids(null);
              setSaved(false);
              setLambdaOverride(null);
            }}
          >
            {GAMES[key].label}
          </button>
        ))}
      </div>

      <label className="field" htmlFor="count">
        Nombre de grilles : <b className="tabular">{count}</b> — mise{' '}
        {formatEuros(count * game.price)}
      </label>
      <input
        id="count"
        type="range"
        min={MIN_GRIDS}
        max={MAX_GRIDS}
        step={1}
        value={count}
        onChange={(e) => setCount(Number(e.target.value))}
      />

      <div className="row" style={{ marginTop: 14 }}>
        <button type="button" className="primary" onClick={generate}>
          Tirer {count} grille{count > 1 ? 's' : ''} · {game.label}
        </button>
      </div>

      <div className="metrics" style={{ marginTop: 10 }}>
        <span className="metric">
          matrice{' '}
          <b>
            {game.k}/{game.n} + {game.bk}/{game.bmax}
          </b>
        </span>
        <span className="metric">
          λ <b className="tabular">{formatDecimal(lambda, 3)}</b>
          {lambdaOverride === null ? ' (dérivé)' : ' (forcé)'}
        </span>
        <span className="metric">
          recouvrement max <b>{formatInt(maxOverlapFor(game))}</b>
        </span>
      </div>

      {game.key === 'eurodreams' && (
        <div className="notice warn">
          <strong>EuroDreams verse des montants fixes.</strong> Le partage n’intervient
          qu’au-delà de trois gagnants au rang 1, donc λ tombe à 0,032 : ces grilles sont
          un tirage quasi uniforme sous contraintes structurelles. Le gain réel de la
          méthode y est de <strong>+0,4 %</strong>, contre +66 % au Loto.
        </div>
      )}

      <div className="row" style={{ marginTop: 10 }}>
        <button type="button" className="ghost small" onClick={() => setAdvanced(!advanced)}>
          {advanced ? 'Masquer' : 'Afficher'} le panneau avancé
        </button>
      </div>

      {advanced && (
        <div className="card">
          <h3>λ — à ne forcer que pour expérimenter</h3>
          <p className="small dim" style={{ marginTop: 0 }}>
            λ est dérivé de l’exposition au partage du jeu ({formatDecimal(game.sharingExposure, 2)}
            ), pas choisi. Au-delà de {formatDecimal(LAMBDA_CEILING, 1)} les grilles
            deviennent si systématiquement hautes qu’elles se remettent à ressembler à
            celles des autres joueurs appliquant la même logique — l’effet s’inverse.
          </p>
          <label className="field" htmlFor="lambda">
            λ = <b className="tabular">{formatDecimal(lambda, 3)}</b>
          </label>
          <input
            id="lambda"
            type="range"
            min={0}
            max={3}
            step={0.05}
            value={lambda}
            onChange={(e) => setLambdaOverride(Number(e.target.value))}
          />
          {lambdaOverride !== null && lambdaOverride > LAMBDA_CEILING && (
            <div className="notice warn">
              Au-delà de {formatDecimal(LAMBDA_CEILING, 1)}, forcer λ est contre-productif.
            </div>
          )}
          {lambdaOverride !== null && (
            <button type="button" className="ghost" onClick={() => setLambdaOverride(null)}>
              Revenir à λ dérivé ({formatDecimal(game.lambda, 3)})
            </button>
          )}
        </div>
      )}

      {error !== null && <div className="notice warn">{error}</div>}

      {grids !== null && (
        <>
          <h2>
            Portefeuille du {formatDateLong(next.date)}
          </h2>
          <div className="notice">
            <strong>Jouez la première sortie.</strong> Ne relancez pas la génération pour
            « avoir mieux » : trier a posteriori réintroduit exactement le biais humain que
            le procédé élimine.
          </div>

          {grids.map((g, i) => (
            <GridCard
              key={`${i}-${g.main.join('-')}`}
              game={game}
              grid={g}
              index={i + 1}
              actions={
                <button
                  type="button"
                  className="ghost"
                  onClick={() => {
                    void navigator.clipboard?.writeText(gridText(game, g));
                  }}
                >
                  Copier
                </button>
              }
            />
          ))}

          <div className="metrics">
            {overlap !== null && (
              <span className="metric">
                recouvrement maximal entre grilles <b>{formatInt(overlap)}</b> numéro
                {overlap > 1 ? 's' : ''}
              </span>
            )}
            {elapsed !== null && (
              <span className="metric">
                généré en <b>{formatDecimal(elapsed, 0)} ms</b>
              </span>
            )}
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <button type="button" onClick={saveAll} disabled={saved}>
              {saved ? 'Enregistré ✓' : `Enregistrer ${grids.length} grille${grids.length > 1 ? 's' : ''}`}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => {
                void navigator.clipboard?.writeText(
                  grids.map((g) => gridText(game, g)).join('\n'),
                );
              }}
            >
              Tout copier
            </button>
          </div>
        </>
      )}

      <p className="footnote">
        Entropie système via <code>crypto.getRandomValues</code>. Le générateur ne consulte
        jamais les fréquences de sortie passées : elles ne portent aucune information sur
        le tirage suivant.
      </p>
    </div>
  );
}
